import { getAuth } from "firebase/auth"

interface BaseEmailDTO {
    email: string
}

export interface ReminderDTO {
    email: string
    orgName: string
    requestedAmount: number
    link: string
    deadline?: string | null
}

export interface DecisionDTO extends BaseEmailDTO {
    declineReason?: string
    grantAmount?: number
    acceptReason?: string
}

export interface OwnershipTransferSubmittedToNewOwnerDTO extends BaseEmailDTO {
    userName: string
    orgName: string
    loginUrl: string
}

export interface OwnershipTransferRequestSubmittedToAdminsDTO
    extends BaseEmailDTO {
    orgName: string
    requesterName: string
    newOwnerName: string
    reviewUrl: string
}

export interface OwnershipTransferRequestDeniedDTO extends BaseEmailDTO {
    requesterName: string
    orgName: string
    proposedNewOwnerName: string
    denialReason?: string
}

export interface OwnershipTransferCompletedDTO extends BaseEmailDTO {
    newOwnerName: string
    orgName: string
    oldOwnerName: string
    transferDate: string
    dashboardUrl: string
}

export interface SecondaryEmailReminderDTO extends BaseEmailDTO {
    userName: string
    orgName: string
    loginUrl: string
}

export interface PasswordResetNotificationDTO {
    recipientEmail: string
    firstName: string
    email: string
}

export interface PasswordResetRequestDTO {
    email: string
    firstName?: string
    subject?: string
}

const API_KEY = import.meta.env.VITE_API_KEY
const BASE_URL = import.meta.env.VITE_EMAIL_SERVICE_BASE_URL

const getFirebaseIdToken = async (): Promise<string> => {
    const auth = getAuth()
    await auth.authStateReady()

    const user = auth.currentUser
    if (!user) {
        throw new Error("User not authenticated")
    }

    return user.getIdToken()
}

const getEmailServiceHeaders = async (
    requireAuth: boolean = true
): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    }

    if (API_KEY) {
        headers["X-API-Key"] = API_KEY
    }

    if (requireAuth) {
        const token = await getFirebaseIdToken()
        headers.Authorization = `Bearer ${token}`
    }

    return headers
}

export const sendReminders = async (dtos: ReminderDTO[]) => {
    try {
        const headers = await getEmailServiceHeaders()
        const response = await fetch(BASE_URL + "/api/mail/send-reminders", {
            method: "POST",
            headers,
            body: JSON.stringify({ reminders: dtos }),
        })

        if (!response.ok) {
            throw new Error("Failed to send reminders")
        }

        return response.json()
    } catch (error) {
        throw error
    }
}

export const sendDeclineEmail = async (dtos: DecisionDTO[]) => {
    try {
        const headers = await getEmailServiceHeaders()
        const response = await fetch(
            BASE_URL + "/api/mail/send-decline-email",
            {
                method: "POST",
                headers,
                body: JSON.stringify({ decisions: dtos }),
            }
        )

        if (!response.ok) {
            throw new Error("Failed to send decline emails")
        }

        return response.json()
    } catch (error) {
        throw error
    }
}

export const sendAcceptEmail = async (dtos: DecisionDTO[]) => {
    try {
        const headers = await getEmailServiceHeaders()
        const response = await fetch(BASE_URL + "/api/mail/send-accept-email", {
            method: "POST",
            headers,
            body: JSON.stringify({ decisions: dtos }),
        })

        if (!response.ok) {
            throw new Error("Failed to send accept emails")
        }

        return response.json()
    } catch (error) {
        throw error
    }
}

export const sendOwnershipTransferSubmittedToNewOwner = async (
    dtos: OwnershipTransferSubmittedToNewOwnerDTO[]
) => {
    try {
        const headers = await getEmailServiceHeaders()
        const response = await fetch(
            BASE_URL +
                "/api/mail/send-ownership-transfer-submitted-to-new-owner",
            {
                method: "POST",
                headers,
                body: JSON.stringify({ dtos }),
            }
        )

        if (!response.ok) {
            throw new Error(
                "Failed to send ownership transfer submitted emails"
            )
        }

        return response.json()
    } catch (error) {
        throw error
    }
}

export const sendOwnershipTransferRequestSubmittedToAdmins = async (
    input: OwnershipTransferRequestSubmittedToAdminsDTO[] | { userId: string }
) => {
    try {
        const headers = await getEmailServiceHeaders()
        const bodyPayload = Array.isArray(input) ? { dtos: input } : input
        const response = await fetch(
            BASE_URL +
                "/api/mail/send-ownership-transfer-request-submitted-to-admins",
            {
                method: "POST",
                headers,
                body: JSON.stringify(bodyPayload),
            }
        )

        if (!response.ok) {
            throw new Error(
                "Failed to send ownership transfer request to admins emails"
            )
        }

        return response.json()
    } catch (error) {
        throw error
    }
}

export const sendOwnershipTransferRequestDenied = async (
    dtos: OwnershipTransferRequestDeniedDTO[]
) => {
    try {
        const headers = await getEmailServiceHeaders()
        const response = await fetch(
            BASE_URL + "/api/mail/send-ownership-transfer-request-denied",
            {
                method: "POST",
                headers,
                body: JSON.stringify({ dtos }),
            }
        )

        if (!response.ok) {
            throw new Error("Failed to send ownership transfer denied emails")
        }

        return response.json()
    } catch (error) {
        throw error
    }
}

export const sendOwnershipTransferCompleted = async (
    dtos: OwnershipTransferCompletedDTO[]
) => {
    try {
        const headers = await getEmailServiceHeaders()
        const response = await fetch(
            BASE_URL + "/api/mail/send-ownership-transfer-completed",
            {
                method: "POST",
                headers,
                body: JSON.stringify({ dtos }),
            }
        )

        if (!response.ok) {
            throw new Error(
                "Failed to send ownership transfer completed emails"
            )
        }

        return response.json()
    } catch (error) {
        throw error
    }
}

export const sendSecondaryEmailReminder = async (
    dtos: SecondaryEmailReminderDTO[]
) => {
    try {
        const headers = await getEmailServiceHeaders()
        const response = await fetch(
            BASE_URL + "/api/mail/send-secondary-email-reminder",
            {
                method: "POST",
                headers,
                body: JSON.stringify({ dtos }),
            }
        )

        if (!response.ok) {
            throw new Error("Failed to send secondary email reminders")
        }

        return response.json()
    } catch (error) {
        throw error
    }
}

export const sendPasswordResetNotification = async (
    dtos: PasswordResetNotificationDTO[]
) => {
    try {
        const headers = await getEmailServiceHeaders()
        const response = await fetch(
            BASE_URL + "/api/mail/send-password-reset-notification",
            {
                method: "POST",
                headers,
                body: JSON.stringify({ passwordResetNotifications: dtos }),
            }
        )

        if (!response.ok) {
            throw new Error("Failed to send password reset notifications")
        }

        return response.json()
    } catch (error) {
        throw error
    }
}

export const sendPasswordResetRequest = async (
    dtos: PasswordResetRequestDTO[]
) => {
    try {
        const headers = await getEmailServiceHeaders(false)
        const response = await fetch(
            BASE_URL + "/api/mail/send-password-reset-request",
            {
                method: "POST",
                headers,
                body: JSON.stringify({ passwordResetRequests: dtos }),
            }
        )

        if (!response.ok) {
            throw new Error("Failed to send password reset request")
        }

        return response.json()
    } catch (error) {
        throw error
    }
}
